import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, RotateCw, Pencil, Loader2 } from 'lucide-react';
import type { ReplayBundleV1 } from '@arrowise/shared';

type Speed = 1 | 2 | 5 | 10;

interface ReplayControlsProps {
    bundle: ReplayBundleV1;
    t: number;
    setT: (t: number) => void;
    isPlaying: boolean;
    setIsPlaying: (playing: boolean) => void;
    speed: Speed;
    setSpeed: (speed: Speed) => void;
    currentStep?: any;
    gradingStatus: { graded: boolean; canGrade: boolean; hasCompleteAssessment: boolean } | null;
    onOpenAssessmentDialog: () => void;
    loadingAssessment?: boolean;
}

export default function ReplayControls({
    bundle,
    t,
    setT,
    isPlaying,
    setIsPlaying,
    speed,
    setSpeed,
    currentStep,
    gradingStatus,
    onOpenAssessmentDialog,
    loadingAssessment = false
}: ReplayControlsProps) {
    const duration = bundle.session.durationMs;

    return (
        <TooltipProvider>
            <Card>
                <CardContent className="py-4">
                    {/* First Row: Controls */}
                    <div className="flex items-center gap-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="icon" variant="outline" onClick={() => setIsPlaying(!isPlaying)}>
                                    {isPlaying ? <Pause className="size-4" /> : <Play className="size-4" />}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Play/Pause</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" onClick={() => setT(0)}>
                                    <RotateCw className="size-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Restart</TooltipContent>
                        </Tooltip>
                        <div className="ml-4 flex-1">
                            <Slider
                                min={0}
                                max={Math.max(1, duration)}
                                value={[Math.min(Math.max(0, t), Math.max(1, duration))]}
                                onValueChange={(vals) => {
                                    const val = Array.isArray(vals) ? vals[0] : (vals as any);
                                    if (typeof val === 'number') setT(val);
                                }}
                            />
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                            <Button size="sm" variant={speed === 1 ? 'default' : 'outline'} onClick={() => setSpeed(1)}>1x</Button>
                            <Button size="sm" variant={speed === 2 ? 'default' : 'outline'} onClick={() => setSpeed(2)}>2x</Button>
                            <Button size="sm" variant={speed === 5 ? 'default' : 'outline'} onClick={() => setSpeed(5)}>5x</Button>
                            <Button size="sm" variant={speed === 10 ? 'default' : 'outline'} onClick={() => setSpeed(10)}>10x</Button>
                        </div>
                    </div>

                    {/* Second Row: Current Step and Assessment */}
                    <div className="flex items-center justify-between mt-2">
                        <div className="flex-1"></div>
                        <div className="flex flex-col items-center flex-1">
                            <div className="text-xs text-muted-foreground">
                                <span><b>Current Step:</b> {currentStep ? currentStep.title || `Step ${currentStep.index + 1}` : ''}</span>
                            </div>
                            {currentStep && (currentStep as any).description ? (
                                <div className="mt-1">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button size="sm" variant="link" className="underline text-xs h-auto p-0">See step description</Button>
                                        </PopoverTrigger>
                                        <PopoverContent side="bottom" align="center" className="max-w-md whitespace-pre-wrap">
                                            <div className="text-sm">{(currentStep as any).description}</div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            ) : null}
                        </div>
                        <div className="flex-1 flex justify-end">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            if (!gradingStatus?.graded) {
                                                onOpenAssessmentDialog();
                                            }
                                        }}
                                        disabled={gradingStatus?.graded || loadingAssessment}
                                        className={`flex items-center gap-2 ${gradingStatus?.graded ? 'opacity-50 cursor-not-allowed hover:bg-background hover:border-border' : ''}`}
                                    >
                                        {loadingAssessment ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Pencil className="w-4 h-4" />
                                        )}
                                        {loadingAssessment ? 'Loading...' : 'Assess'}
                                    </Button>
                                </TooltipTrigger>
                                {gradingStatus?.graded && (
                                    <TooltipContent sideOffset={8}>
                                        <p>Cannot assess a session marked as assessed</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </TooltipProvider>
    );
}
